import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getKVConfigs } from '@/apis';

const CURRENT_PLATFORM_ID = 1;

export function useKVConfig(key: string, suspense = false) {
  const {
    data: configs,
    refetch,
    ...extraProps
  } = useQuery(
    [getKVConfigs.name],
    () => getKVConfigs({ page_size: 9999 }).then((res) => res.data?.list ?? []),
    {
      suspense,
    },
  );

  const config = useMemo(() => {
    // 过滤出包含当前平台 ID 的配置项
    const matchedConfig = configs?.find(
      (item) =>
        item.key === key && item.platform_ids?.includes(CURRENT_PLATFORM_ID),
    );

    if (!matchedConfig) {
      return undefined;
    }

    return matchedConfig;
  }, [configs, key]);

  const value = useMemo(() => {
    if (!config?.value) {
      return config?.value;
    }

    try {
      // 尝试解析为 JSON 对象
      const parsedValue = JSON.parse(config.value);

      // 如果是对象且包含平台 ID 对应的键，返回对应平台的值
      if (
        typeof parsedValue === 'object' &&
        parsedValue !== null &&
        !Array.isArray(parsedValue) &&
        String(CURRENT_PLATFORM_ID) in parsedValue
      ) {
        return parsedValue[String(CURRENT_PLATFORM_ID)];
      }

      // 否则返回整个解析后的值
      return parsedValue;
    } catch {
      // 如果解析失败，说明是纯文本，直接返回
      return config.value;
    }
  }, [config]);

  return {
    value,
    refetch,
    ...extraProps,
  };
}
